import type { COMMUNITY_TYPE } from '@space-shell/modules/communities/accessAndPrivacy/constants'

export type CommunityProps = {
  id: number
  name: string
  isLocked: boolean
  url?: string
  description?: string
  imageURL: string
  coverImageURL?: string
  imageDescription: string
  coverDescription?: string
  permissions?: CommunityMemberPermissions
  owner: string
  lastPostDate: number
  followersCount: number
  isOwner?: boolean
  isFollowing?: boolean
  ownerId?: number
  ownerUcode?: string
  ownerPictureUrl?: string
  isOwnerOrFollower?: boolean
  icon?: string
  idGroup?: number
  hasUnseenNewsOrCommentReplies?: boolean
  order?: number
  type: COMMUNITY_TYPE
  isCollaborative?: boolean
  isDefaultEnableComments?: boolean
  isHide?: boolean
  isModerationCommunity?: boolean
}

export type CommunitiesProps = {
  nextPageToken: string
  data: CommunityProps[]
}

export type EditCommunityProps = {
  name: string
  description: string
  icon: string
  groupId: number
  id: number
  type: COMMUNITY_TYPE
  imageDescription?: string
  imageURL?: string
  coverImage?: string
  coverDescription?: string
  image?: string
  collaborativeContent: boolean
  hide: boolean
}

export type CreateCommunityProps = {
  name: string
  description: string
  image: string
  imageDescription: string
  coverImage: string
  coverDescription: string
  icon: string
  groupId: number
  type: COMMUNITY_TYPE
  collaborativeContent: boolean
  defaultEnableComments: boolean
  hide: boolean
  isModerationCommunity?: boolean
}

export type CommunityGroupCreateResponse = {
  id: number
  name: string
  slug: string
  order: number
}

export type CommunityGroupGetResponse = [
  {
    communities: {
      data: CommunityProps[]
    }
  } & CommunityGroupCreateResponse
]

export type MembershipCoursesAdmin = {
  id: number
}

export type CommunityAdminProps = {
  followersCount: number
  id: number
  isOwnerOrFollower: boolean
  lastPostDate: number
  imageURL: string
  membershipCourses: MembershipCoursesAdmin[]
  name: string
  owner: string
}

export type CommunitiesRequestProps = {
  nextPageToken: string
  data: CommunityAdminProps[]
}

export enum CommunitiesActions {
  ADD = 'add',
  EDIT = 'edit',
  DELETE = 'delete'
}

export type CommunitiesEventsPayloadProps = Record<CommunitiesActions, unknown>

export type PutUnblockUserParams = {
  communityId: number
  idUser: number
}

export type GetBlockedFollowersParams = {
  communityId: number
  pageParam?: number
  rows?: number
  query?: string
}

export enum PermissionActions {
  ACCESS_AND_PRIVACY = 'ACCESS_AND_PRIVACY',
  ADMIN_UPDATE = 'ADMIN_UPDATE',
  BLOCK_ADMIN_CREATE = 'BLOCK_ADMIN_CREATE',
  BLOCK_CREATE = 'BLOCK_CREATE',
  BLOCK_DELETE = 'BLOCK_DELETE',
  BLOCK_MEMBER_CREATE = 'BLOCK_MEMBER_CREATE',
  BLOCK_MODERATOR_CREATE = 'BLOCK_MODERATOR_CREATE',
  BLOCK_READ = 'BLOCK_READ',
  COLLECTION_DELETE = 'COLLECTION_DELETE',
  COLLECTION_UPDATE = 'COLLECTION_UPDATE',
  COMMENT_DELETE = 'COMMENT_DELETE',
  COMMENT_HIDE = 'COMMENT_HIDE',
  COMMENT_SORTITION_CREATE = 'COMMENT_SORTITION_CREATE',
  FOLLOW_REQUEST_READ = 'FOLLOW_REQUEST_READ',
  FOLLOW_REQUEST_UPDATE = 'FOLLOW_REQUEST_UPDATE',
  GROUP_CREATE = 'GROUP_CREATE',
  GROUP_DELETE = 'GROUP_DELETE',
  GROUP_READ = 'GROUP_READ',
  GROUP_UPDATE = 'GROUP_UPDATE',
  IMPORT_MEMBER = 'IMPORT_MEMBER',
  MEMBER_DELETE = 'MEMBER_DELETE',
  MEMBER_ROLE_READ = 'MEMBER_ROLE_READ',
  MEMBER_UPDATE = 'MEMBER_UPDATE',
  MODERATOR_UPDATE = 'MODERATOR_UPDATE',
  PIN_CREATE = 'PIN_CREATE',
  PIN_DELETE = 'PIN_DELETE',
  POST_CREATE = 'POST_CREATE',
  POST_DELETE = 'POST_DELETE',
  POST_REVIEW_UPDATE = 'POST_REVIEW_UPDATE',
  SCHEDULED_POST_CREATE = 'SCHEDULED_POST_CREATE',
  SCHEDULED_POST_DELETE = 'SCHEDULED_POST_DELETE',
  SCHEDULED_POST_READ = 'SCHEDULED_POST_READ',
  STRIKE_CREATE = 'STRIKE_CREATE',
  STRIKE_DELETE = 'STRIKE_DELETE',
  STRIKE_READ = 'STRIKE_READ'
}

export enum CommunityMemberPermissionsGroup {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  MODERATOR = 'MODERATOR',
  FOLLOWER = 'FOLLOWER',
  VIEWER = 'VIEWER'
}

export type CommunityMemberRoles = `${Exclude<
  CommunityMemberPermissionsGroup,
  CommunityMemberPermissionsGroup.OWNER | CommunityMemberPermissionsGroup.VIEWER
>}`

export type CommunityManagementRoles = Exclude<
  CommunityMemberPermissionsGroup,
  CommunityMemberPermissionsGroup.FOLLOWER | CommunityMemberPermissionsGroup.VIEWER
>

export type CommunityMemberPermissions = {
  group: CommunityMemberPermissionsGroup
  actions?: Array<PermissionActions>
}

export type CommunityMember = {
  id: number
  userName: string
  name?: string
  email?: string
  imagePath?: string
  memberType: CommunityMemberPermissionsGroup
  ucode: string
  idSecurity: number
  hasImage?: boolean
  hasCoverImage?: boolean
  hashImportedId?: string
  expirationDate?: number
}

export type UserBlockedFollowers = Pick<
  CommunityMember,
  'id' | 'name' | 'userName' | 'imagePath'
> & {
  email?: string
}

export type ResponseGetBlockedFollowers = {
  data: UserBlockedFollowers[]
  size: number
  page: number
}

export type IPropsAssociateOrDissociate = {
  idCollection: number
  idCourse: number
  bind: boolean
}

export type PropsAssociateOrDissociateItemList = {
  collectionCourses: IPropsAssociateOrDissociate[]
}

export type CommunityCourseItemProps = {
  idProduct: number
  productName: string
  description: string
  backgroundImageFinal: string
  hasCommunityAssociated: boolean
  countCommunityAssociated: number
}

export type CommunitiesCourseListProps = {
  content: CommunityCourseItemProps[]
}

export type CommunitiesCourseListResponse = {
  numberOfElements: number
  page: number
  totalElements: number
  totalPages: number
  numberOfAssociatedProducts: number
  content: CommunityCourseItemProps[]
}

export type GetFollowersRolesRequestFilters = {
  role: CommunityMemberRoles
  rows?: number
  nextPageToken?: string
  q?: string
}

export type GetFollowersRolesRequestResponse = {
  data: CommunityMember[]
  nextPageToken: string
}

export type EditFollowerRoleArgs = {
  communityId: number
  userId: number
  body: Record<'memberType', CommunityMemberRoles>
}

export type CreateCommunityPermission = {
  hasPermission: boolean
}

export type CreateCommunityPermissionArgs = {
  featureName: string
  enabled?: boolean
}

export type DeleteFollowerArgs = {
  communityId: number
  ucode: string
}

export type ExpirationDateArgs = {
  hashImportedId: string
  ucode: string
  expirationDate: string
}

export type MembershipInfoProps = {
  membershipId: string
  courseId?: number
  lessonHash?: string
  courseTag?: string
}

export type PostProps = {
  id: number
  authorName: string
  authorImagePath: string
  authorHasImage: boolean
  authorMemberType: string
  title: string
  pubDate: number
  isAuthor: boolean
  postType: number
  authorId: number
  membershipInfo: MembershipInfoProps
  authorUcode?: string
  ownerCollectionName?: string
  isFixed?: boolean
  link?: string
  description?: string
  richDescription?: string
  canUserComment?: boolean
  mostRecentCommentDate?: number
  authorImageFinalUrl?: string
  mediaHeight?: number
  mediaWidth?: number
  commentsCount?: number
  isCommentsEnabled?: boolean
  communityId?: number
  content?: string
  domainURL?: string
  domainImage?: string
  hasUnseenReactions?: boolean
  ownerCollectionId?: number
  scheduledPubDate?: string
  scheduledPubTimeZoneId?: string
  authorFeaturedMemberRank?: number
  trending?: boolean
  viewsCount?: number
}

export type CommunityPosts = {
  id: number
  owner: string
  name: string
  isOwner: boolean
  news: {
    data: PostProps[]
    page: number
    size: number
  }
  hasNewsWithUnseenReactions?: boolean
}
